/**
 * Enterprise + industrial network gear — Meraki MG, Westermo, Cisco IR.
 *
 * Three brands that own narrower niches than the broad cellular catalog:
 *   • Cisco Meraki MG41 — dedicated LTE-Advanced Pro gateway, cloud-
 *     managed, IP67. Sits in cellular-antenna alongside the other
 *     LTE CPEs.
 *   • Westermo MRD-455 — Swedish industrial 4G LTE router with
 *     DIN-rail + isolated power + serial. Heavy in oil/gas/SCADA.
 *   • Cisco Catalyst IR1101 — modular industrial integrated services
 *     router; LTE is OPTIONAL (slot-pluggable module). Primary
 *     identity is a router, so it lives in the 'router' category
 *     with `cellular` field set to 'lte-cat6' for the standard
 *     pluggable LTE module.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const ENTERPRISE_INDUSTRIAL_NETWORK: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // Cisco Meraki MG41 — cellular gateway (cellular-antenna)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://meraki.cisco.com/product/cellular/integrated-antenna/mg41/
  // SRC: https://meraki.cisco.com/product-collateral/mg41-series-datasheet/
  // SRC: https://www.cloudwifiworks.com/mg41.asp
  //      Meraki MG41: embedded Cat 18 LTE-Advanced Pro modem with up
  //      to 1.2 Gbps DL / 150 Mbps UL throughput, 2× nano-SIM (4FF)
  //      with auto-failover, 2× GbE Ethernet, IP67 rated for outdoor
  //      deployments, PoE-in OR DC powered, automatic firmware
  //      upgrades + scheduling via the Cisco Meraki Dashboard.
  //      MG51 / MG52 are the 5G NSA / 5G SA tier in the family.
  {
    id: 'cisco-meraki-mg41',
    manufacturer: 'Cisco Meraki',
    model: 'MG41',
    category: 'cellular-antenna',
    tagline:
      'Meraki MG41 — Cat 18 LTE-A Pro gateway, dual nano-SIM, 2× GbE, IP67, cloud-managed',
    verified: true,
    datasheetUrl: 'https://meraki.cisco.com/product-collateral/mg41-series-datasheet/',
    specs: {
      technology: 'lte-cat18',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2, // integrated antenna variants ship with built-in whips
      simSlots: 2,
      ipRating: 'IP67',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Westermo MRD-455 — Swedish industrial 4G LTE (cellular-antenna)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.westermo.com/products/routers/wireless-routers/mrd-455
  // SRC: https://www.westermo.com/us/support/product-support/mrd-455
  // SRC: https://eworldme.com/Westermo_MRD_455_Dual_SIM_Industrial_4G_router
  //      MRD-455: industrial 4G/LTE/3G/GPRS router, 2× 10/100 Mbit/s
  //      Ethernet, 1× RS-232 (300 bit/s – 115.2 kbit/s), 2× Mini-SIM
  //      with auto-failover, DIN-rail mountable, isolated 10-60 VDC
  //      power input, GPS antenna port for asset tracking. Targets
  //      SCADA / HMI / PLC remote access over cellular with VPN +
  //      packet inspection firewall, ground-loop isolation on power
  //      AND Ethernet AND serial ports.
  {
    id: 'westermo-mrd-455',
    manufacturer: 'Westermo',
    model: 'MRD-455',
    category: 'cellular-antenna',
    tagline:
      'MRD-455 — industrial 4G LTE, 2× FE + RS-232, dual SIM, isolated 10-60 VDC, GPS',
    verified: true,
    datasheetUrl: 'https://www.westermo.com/products/routers/wireless-routers/mrd-455',
    specs: {
      technology: 'lte-cat4',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Cisco Catalyst IR1101 — modular industrial router (router)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.cisco.com/c/en/us/products/collateral/routers/1101-industrial-integrated-services-router/datasheet-c78-741709.html
  // SRC: https://www.cisco.com/c/en/us/td/docs/IIOT/routers/ir1101/hw-install-guide/b-ir1101-hig/m-product-overview.html
  // SRC: https://www.cisco.com/c/en/us/td/docs/IIOT/routers/ir1101/hw-install-guide/b-ir1101-hig/m-technical-specifications.html
  //      Cisco Catalyst IR1101: compact FirstNet-capable modular
  //      industrial integrated services router, 2× GbE, dual-band
  //      Wi-Fi 802.11ac Wave 2, one 4G LTE Cat 6 pluggable cellular
  //      module slot (global bands 700-2600 MHz), 1× USB 2.0, expansion
  //      slot adds: dual LTE pluggables / mSATA SSD FRU / SFP / extra
  //      L2 Ethernet / RJ45 serial / Digital GPIO. 4 GB DRAM, 8 GB
  //      Flash. Integrated 9.6-60 VDC input, -40..+60 °C standard
  //      (-40..+75 °C forced air), type-tested at 85 °C for 16 hrs.
  //      Shock + vibration + dust + humidity + ESD rated.
  {
    id: 'cisco-ir1101',
    manufacturer: 'Cisco',
    model: 'Catalyst IR1101',
    category: 'router',
    tagline:
      'Catalyst IR1101 — modular industrial router, 2× GbE, optional Cat 6 LTE + Wi-Fi 5, -40..+60 °C',
    verified: true,
    datasheetUrl:
      'https://www.cisco.com/c/en/us/products/collateral/routers/1101-industrial-integrated-services-router/datasheet-c78-741709.html',
    specs: {
      wanPorts: 1,
      lanPorts: 1,
      lanPortSpeed: '1G',
      wifi: 'wifi5',
      cellular: 'lte-cat6', // optional pluggable module — IR1101 is router-first
      formFactor: 'industrial-DIN',
      powerConsumptionW: 25,
      management: 'cli',
    },
  },
];
